import { z } from "zod";
import { EpochMillisSchema } from "./common.js";
/**
 * CMS de contenu de site (SITE_SPEC — pages/annonces/héros éditables depuis l'admin).
 *
 * Une entrée de contenu est identifiée par une `key` stable (slug) et porte un `type`
 * (hero | announcement | page | section), un titre, un corps (markdown/HTML), un drapeau
 * `enabled` de publication et un `order` de tri. Persistée dans la table `web_content`
 * (bootstrap CREATE IF NOT EXISTS côté BFF, à côté du schéma d'auth).
 *
 * Surface :
 *   - PUBLIC : GET du contenu publié (enabled = true) — pages/annonces/héros affichés au visiteur.
 *   - ADMIN  : liste complète (publié + brouillons), upsert, suppression.
 *
 * Les agents Fill (BFF `routes/content.ts` + web CMS) construisent contre CES types — jamais
 * redéfinis ailleurs.
 */
/** Type de bloc de contenu éditorial. */
export const CONTENT_TYPES = ["hero", "announcement", "page", "section"];
export const SiteContentTypeSchema = z.enum(CONTENT_TYPES);
/**
 * Une entrée de contenu de site (miroir 1:1 d'une ligne `web_content`).
 *  - key       : slug stable et unique (identifie l'entrée ; ex « accueil-hero », « annonce-maj »).
 *  - type      : nature du bloc (hero/announcement/page/section).
 *  - title     : titre affiché (FR).
 *  - body      : corps riche — markdown ou HTML (le rendu tranche côté web).
 *  - enabled   : publié (true) ou brouillon masqué au public (false).
 *  - order     : ordre de tri croissant au sein d'un type (petit = premier).
 *  - updatedAt : epoch ms de la dernière écriture.
 */
export const SiteContentSchema = z.object({
    key: z.string().min(1).max(128),
    type: SiteContentTypeSchema,
    title: z.string().max(255),
    body: z.string(),
    enabled: z.boolean(),
    order: z.number().int().default(0),
    updatedAt: EpochMillisSchema,
});
/** Réponse liste de contenu (publié pour le public, complet pour l'admin). */
export const SiteContentListSchema = z.object({
    items: z.array(SiteContentSchema),
});
/** Filtre de liste : par type et/ou état de publication (admin). Tout optionnel. */
export const SiteContentListReqSchema = z.object({
    type: SiteContentTypeSchema.optional(),
    enabledOnly: z.boolean().optional(),
});
/**
 * Corps d'upsert (création ou mise à jour) d'une entrée de contenu (admin).
 * `updatedAt` est posé côté serveur (non fourni par le client) ; `order` par défaut 0.
 */
export const SiteContentUpsertReqSchema = z.object({
    key: z.string().min(1).max(128),
    type: SiteContentTypeSchema,
    title: z.string().max(255),
    body: z.string(),
    enabled: z.boolean().default(true),
    order: z.number().int().default(0),
});
//# sourceMappingURL=content.js.map